import React, { useState, useEffect, useCallback } from 'react';
import emailjs from '@emailjs/browser';
import AdminHeader from './components/AdminHeader';
import MetricsSummary from './components/MetricsSummary';
import TabNavigation from './components/TabNavigation';
import OverviewTab from './tabs/OverviewTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import VisitorsTab from './tabs/VisitorsTab';
import ClicksTab from './tabs/ClicksTab';
import MessagesTab from './tabs/MessagesTab';
import InsightsTab from './tabs/InsightsTab';
import ReplyModal from './modals/ReplyModal';
import TelemetryModal from './modals/TelemetryModal';
import './AnalyticsDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AnalyticsDashboard = ({ token, onLogout }) => {
  const [stats, setStats] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    todayViews: 0,
    projectClicks: 0,
    topReferrers: [],
    topCountries: [],
    last7DaysViews: []
  });
  const [extendedStats, setExtendedStats] = useState({
    deviceDistribution: [],
    browserDistribution: [],
    hourlyTraffic: [],
    topCities: [],
    last30DaysViews: [],
    weeklyComparison: { current: 0, previous: 0, change: 0 }
  });
  const [topProjects, setTopProjects] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [liveData, setLiveData] = useState({ liveCount: 0, activeSessions: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVisitors, setTotalVisitors] = useState(0);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [isTogglingAnalytics, setIsTogglingAnalytics] = useState(false);
  const [allowDevTracking, setAllowDevTracking] = useState(false);
  const [isTogglingAllowDev, setIsTogglingAllowDev] = useState(false);
  const [settingsUpdatedAt, setSettingsUpdatedAt] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyData, setReplyData] = useState({ to: '', subject: '', message: '', id: null, name: '' });
  const [isSendingReply, setIsSendingReply] = useState(false);

  // Telemetry Inspect Modal State
  const [selectedTelemetryVisitor, setSelectedTelemetryVisitor] = useState(null);

  const fetchWithAuth = useCallback(async (endpoint) => {
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      return response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      return { success: false };
    }
  }, [token]);

  const fetchData = useCallback(async (options = {}) => {
    const { manual = false } = options;

    if (manual) {
      setIsRefreshing(true);
    }

    try {
      const [statsData, extendedData, projectsData, visitorsData, eventsData, liveDataRes, messagesData, statusData] = await Promise.all([
        fetchWithAuth('/api/analytics/stats'),
        fetchWithAuth('/api/analytics/extended-stats'),
        fetchWithAuth('/api/analytics/top-projects'),
        fetchWithAuth(`/api/analytics/visitors?page=${page}&limit=15`),
        fetchWithAuth('/api/analytics/events?limit=30'),
        fetchWithAuth('/api/analytics/live'),
        fetchWithAuth('/api/contacts'),
        fetchWithAuth('/api/analytics/status')
      ]);

      if (statsData.success && statsData.stats) setStats(statsData.stats);
      if (extendedData.success && extendedData.extendedStats) setExtendedStats(extendedData.extendedStats);
      if (projectsData.success) setTopProjects(projectsData.topProjects || []);
      if (visitorsData.success) {
        setVisitors(visitorsData.visitors || []);
        setTotalPages(visitorsData.totalPages || 1);
        setTotalVisitors(visitorsData.total || 0);
      }
      if (eventsData.success) setEvents(eventsData.events || []);
      if (liveDataRes.success) setLiveData(liveDataRes);
      if (messagesData.success) setMessages(messagesData.contacts || []);
      if (statusData?.success && typeof statusData.enabled === 'boolean') setAnalyticsEnabled(statusData.enabled);
      if (statusData?.success && typeof statusData.allowDev === 'boolean') setAllowDevTracking(statusData.allowDev);
      if (statusData?.success && statusData.updatedAt) setSettingsUpdatedAt(statusData.updatedAt);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
      if (manual) setIsRefreshing(false);
    }
  }, [fetchWithAuth, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const liveDataRes = await fetchWithAuth('/api/analytics/live');
        if (liveDataRes.success) setLiveData(liveDataRes);
      } catch (error) {}
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchWithAuth]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'Mobile': return '📱';
      case 'Tablet': return '📲';
      default: return '💻';
    }
  };

  const getReferrerIcon = (referrer) => {
    switch (referrer) {
      case 'LinkedIn': return '💼';
      case 'Google': return '🔍';
      case 'GitHub': return '🐙';
      case 'Twitter/X': return '🐦';
      case 'Direct': return '🔗';
      default: return '🌐';
    }
  };

  const getBrowserIcon = (browser) => {
    switch (browser) {
      case 'Chrome': return '🌐';
      case 'Safari': return '🧭';
      case 'Firefox': return '🦊';
      case 'Edge': return '🔷';
      default: return '🌐';
    }
  };

  const getBrowserColor = (browser) => {
    const colors = {
      'Chrome': '#4285f4',
      'Safari': '#00aaff',
      'Firefox': '#ff7139',
      'Edge': '#0078d7',
      'Opera': '#ff1b2d',
      'Unknown': '#888'
    };
    return colors[browser] || '#00FFD1';
  };

  const getCountryFlag = (country) => {
    const flags = {
      'India': '🇮🇳',
      'United States': '🇺🇸',
      'United Kingdom': '🇬🇧',
      'Germany': '🇩🇪',
      'Canada': '🇨🇦',
      'Australia': '🇦🇺',
      'Local': '🏠'
    };
    return flags[country] || '🌍';
  };

  const generateInsights = () => {
    const insights = [];

    if (extendedStats.weeklyComparison) {
      const { change, current, previous } = extendedStats.weeklyComparison;
      if (change > 0) {
        insights.push({ icon: '📈', text: `Traffic increased ${change}% compared to last week (${current} vs ${previous} views)`, type: 'positive' });
      } else if (change < 0) {
        insights.push({ icon: '📉', text: `Traffic decreased ${Math.abs(change)}% compared to last week`, type: 'negative' });
      } else if (current > 0) {
        insights.push({ icon: '➡️', text: `Traffic is stable compared to last week`, type: 'info' });
      }
    }

    if (stats.topReferrers && stats.topReferrers.length > 0) {
      const topRef = stats.topReferrers[0];
      const percentage = stats.totalViews > 0 ? Math.round((topRef.count / stats.totalViews) * 100) : 0;
      insights.push({ icon: getReferrerIcon(topRef._id), text: `${topRef._id} is your top traffic source (${percentage}% of visitors)`, type: 'info' });
    }

    if (extendedStats.deviceDistribution && extendedStats.deviceDistribution.length > 0) {
      const total = extendedStats.deviceDistribution.reduce((sum, d) => sum + d.count, 0);
      const mobile = extendedStats.deviceDistribution.find(d => d._id === 'Mobile');
      const desktop = extendedStats.deviceDistribution.find(d => d._id === 'Desktop');
      if (mobile && total > 0) {
        const mobilePercentage = Math.round((mobile.count / total) * 100);
        insights.push({ icon: '📱', text: `${mobilePercentage}% of visitors browse from mobile devices`, type: 'info' });
      }
      if (desktop && total > 0) {
        const desktopPercentage = Math.round((desktop.count / total) * 100);
        insights.push({ icon: '💻', text: `${desktopPercentage}% of visitors use desktop computers`, type: 'info' });
      }
    }

    if (stats.topCountries && stats.topCountries.length > 0) {
      const topCountry = stats.topCountries[0];
      const percentage = stats.totalViews > 0 ? Math.round((topCountry.count / stats.totalViews) * 100) : 0;
      insights.push({ icon: getCountryFlag(topCountry._id), text: `${topCountry._id} accounts for ${percentage}% of your traffic`, type: 'info' });
    }

    if (topProjects && topProjects.length > 0) {
      insights.push({ icon: '⚡', text: `"${topProjects[0]._id}" is your most clicked project (${topProjects[0].count} clicks)`, type: 'info' });
    }

    if (extendedStats.hourlyTraffic && extendedStats.hourlyTraffic.length > 0) {
      const sorted = [...extendedStats.hourlyTraffic].sort((a, b) => b.count - a.count);
      if (sorted.length > 0) {
        const peakHour = sorted[0]._id;
        const formattedHour = peakHour >= 12 ? `${peakHour === 12 ? 12 : peakHour - 12} PM` : `${peakHour === 0 ? 12 : peakHour} AM`;
        insights.push({ icon: '🕐', text: `Peak traffic hour: ${formattedHour}`, type: 'info' });
      }
    }

    return insights;
  };

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_URL}/api/contacts/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'read' })
      });

      if (response.ok) {
        setMessages(prev => prev.map(msg => msg._id === id ? { ...msg, status: 'read' } : msg));
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleReply = (msg) => {
    setReplyData({
      to: msg.email,
      subject: `Re: ${msg.subject}`,
      message: '',
      name: msg.name,
      id: msg._id
    });
    setReplyModalOpen(true);
  };

  const closeReplyModal = () => {
    setReplyModalOpen(false);
    setReplyData({ to: '', subject: '', message: '', id: null, name: '' });
  };

  const sendReply = async (e) => {
    e.preventDefault();
    setIsSendingReply(true);

    const EMAILJS_SERVICE_ID = 'service_ouq955n';
    const EMAILJS_TEMPLATE_ID = 'template_wnm75bf';
    const EMAILJS_PUBLIC_KEY = 'N7fgACS-m8PSrnVJo';

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_name: replyData.name,
          email: replyData.to,
          from_name: 'Arun L. Kumar',
          subject: replyData.subject,
          message: replyData.message,
          reply_to: 'arunkumar582004@gmail.com'
        },
        EMAILJS_PUBLIC_KEY
      );

      await fetch(`${API_URL}/api/contacts/${replyData.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'replied' })
      });

      setMessages(prev => prev.map(msg => msg._id === replyData.id ? { ...msg, status: 'replied' } : msg));
      closeReplyModal();
      alert('Reply sent successfully!');
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleClearAllMessages = async () => {
    if (!window.confirm('Are you sure you want to delete ALL messages? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/contacts`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessages([]);
        alert(`Deleted ${data.deletedCount} messages successfully!`);
      } else {
        alert('Failed to delete messages: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to clear messages:', error);
      alert('Failed to clear messages. Please try again.');
    }
  };

  const handleResetAllData = async () => {
    if (!window.confirm('⚠️ WARNING: This will delete ALL analytics data including:\n- All visitor records\n- All click events\n- All contact messages\n\nThis cannot be undone. Are you sure?')) return;
    try {
      const response = await fetch(`${API_URL}/api/analytics/clear-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert(`✅ All data cleared!\n- ${data.deleted.visitors} visitors\n- ${data.deleted.events} events\n- ${data.deleted.contacts} messages`);
        window.location.reload();
      } else {
        alert('Failed to clear data: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to reset data:', error);
      alert('Failed to reset data. Please try again.');
    }
  };

  const toggleAnalytics = async () => {
    if (isTogglingAnalytics) return;
    setIsTogglingAnalytics(true);

    try {
      const response = await fetch(`${API_URL}/api/analytics/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: !analyticsEnabled })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setAnalyticsEnabled(Boolean(data.enabled));
        if (data.updatedAt) setSettingsUpdatedAt(data.updatedAt);
      } else {
        alert(data.message || 'Failed to update analytics status');
      }
    } catch (error) {
      console.error('Failed to toggle analytics:', error);
      alert('Failed to update analytics status');
    } finally {
      setIsTogglingAnalytics(false);
    }
  };

  const toggleAllowDevTracking = async () => {
    if (isTogglingAllowDev) return;
    setIsTogglingAllowDev(true);

    const nextValue = !allowDevTracking;

    try {
      const response = await fetch(`${API_URL}/api/analytics/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ allowDev: nextValue })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setAllowDevTracking(Boolean(data.allowDev));
        if (data.updatedAt) setSettingsUpdatedAt(data.updatedAt);
      } else {
        alert(data.message || 'Failed to update dev tracking status');
      }
    } catch (error) {
      console.error('Failed to toggle dev tracking:', error);
      alert('Failed to update dev tracking status');
    } finally {
      setIsTogglingAllowDev(false);
    }
  };

  const unreadMessagesCount = messages.filter((m) => m.status === 'unread' || !m.status).length;

  if (loading) {
    return (
      <div className="admin-loading-screen">
        <div className="cyber-spinner"></div>
        <span>INITIALIZING TELEMETRY DASHBOARD...</span>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-root">
      {/* Top Header */}
      <AdminHeader
        liveData={liveData}
        analyticsEnabled={analyticsEnabled}
        isTogglingAnalytics={isTogglingAnalytics}
        toggleAnalytics={toggleAnalytics}
        allowDevTracking={allowDevTracking}
        isTogglingAllowDev={isTogglingAllowDev}
        toggleAllowDevTracking={toggleAllowDevTracking}
        settingsUpdatedAt={settingsUpdatedAt}
        isRefreshing={isRefreshing}
        fetchData={fetchData}
        handleResetAllData={handleResetAllData}
        onLogout={onLogout}
        formatDate={formatDate}
      />

      <div className="admin-dashboard-container">
        {/* Metric Cards Summary Grid */}
        <MetricsSummary stats={stats} extendedStats={extendedStats} />

        {/* Cyber Navigation Tabs */}
        <TabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          unreadMessagesCount={unreadMessagesCount}
        />

        {/* Tab Viewport */}
        <div className="dashboard-tab-content">
          {activeTab === 'overview' && (
            <OverviewTab
              stats={stats}
              extendedStats={extendedStats}
              visitors={visitors}
              messages={messages}
              setActiveTab={setActiveTab}
              getCountryFlag={getCountryFlag}
              getReferrerIcon={getReferrerIcon}
              getDeviceIcon={getDeviceIcon}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'analytics' && (
            <AnalyticsTab
              extendedStats={extendedStats}
              getDeviceIcon={getDeviceIcon}
              getBrowserIcon={getBrowserIcon}
              getBrowserColor={getBrowserColor}
            />
          )}

          {activeTab === 'visitors' && (
            <VisitorsTab
              visitors={visitors}
              totalVisitors={totalVisitors}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
              setSelectedTelemetryVisitor={setSelectedTelemetryVisitor}
              getCountryFlag={getCountryFlag}
              getDeviceIcon={getDeviceIcon}
              getBrowserIcon={getBrowserIcon}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'clicks' && (
            <ClicksTab
              topProjects={topProjects}
              events={events}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'messages' && (
            <MessagesTab
              messages={messages}
              handleMarkAsRead={handleMarkAsRead}
              handleReply={handleReply}
              handleClearAllMessages={handleClearAllMessages}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'insights' && (
            <InsightsTab generateInsights={generateInsights} />
          )}
        </div>
      </div>

      {/* Reply Modal */}
      <ReplyModal
        replyModalOpen={replyModalOpen}
        closeReplyModal={closeReplyModal}
        replyData={replyData}
        setReplyData={setReplyData}
        sendReply={sendReply}
        isSendingReply={isSendingReply}
      />

      {/* Telemetry Inspection Modal */}
      <TelemetryModal
        selectedTelemetryVisitor={selectedTelemetryVisitor}
        setSelectedTelemetryVisitor={setSelectedTelemetryVisitor}
        getCountryFlag={getCountryFlag}
        getDeviceIcon={getDeviceIcon}
        getBrowserIcon={getBrowserIcon}
        formatFullDate={formatFullDate}
      />
    </div>
  );
};

export default AnalyticsDashboard;
